import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share2, ChevronRight, ArrowLeft, ArrowRight, User, Users, Book,
  Globe, Loader, Feather, Home, Info, Mail, Heart, MapPin, Sparkles, Copy,
  RefreshCw, Check, HandHeart, ShieldCheck,
  Sunrise, HeartPulse, Briefcase, GraduationCap, Leaf, Anchor, Star, Sun,
  Clock, Sparkle,
} from 'lucide-react';
import { KofiButton } from "react-kofi-button";
import { GB, ES, CN, IN, SA, FR, BD, RU, PT, PK, DE, JP, TZ, KR, IT } from 'country-flag-icons/react/3x2';

/**
 * Groq Cloud — OpenAI-compatible chat completions endpoint.
 * Models are tried in order; if one is down, rate limited (429) or errors (5xx),
 * the next one in the list is used automatically.
 */
// Secure Server Proxy Endpoints (Hosted on your Hostinger server)
const API_PRAYER_ENDPOINT = '/api/generate-prayer.php';
const GROQ_MODELS = ['openai/gpt-oss-120b', 'openai/gpt-oss-20b'] as const;

/** Statuses worth retrying on the next model rather than failing outright. */
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504, 529]);

const STEPS = ['NAME', 'TOPIC', 'LANGUAGE', 'PRAYER'] as const;

const PROMPTS = {
  NAME: "Let's start with your name, city, and who this prayer is for",
  TOPIC: "What would you like to pray about?",
  LANGUAGE: "Select the language for your prayer",
  PRAYER: "Here's your personalized prayer and a relevant Bible verse"
};

/** Short label + hint used by the redesigned stepper rail. */
const STEP_META: Record<string, { label: string; hint: string }> = {
  NAME: { label: 'Details', hint: 'Who and where' },
  TOPIC: { label: 'Topic', hint: 'What is on your heart' },
  LANGUAGE: { label: 'Language', hint: 'How you want to hear it' },
  PRAYER: { label: 'Prayer', hint: 'Read and share' },
};

const LANGUAGES = [
  { code: 'en', name: 'English', flag: GB, popularity: 1 },
  { code: 'es', name: 'Spanish', flag: ES, popularity: 0.9 },
  { code: 'zh', name: 'Chinese (Mandarin)', flag: CN, popularity: 0.85 },
  { code: 'hi', name: 'Hindi', flag: IN, popularity: 0.8 },
  { code: 'ar', name: 'Arabic', flag: SA, popularity: 0.75 },
  { code: 'fr', name: 'French', flag: FR, popularity: 0.7 },
  { code: 'bn', name: 'Bengali', flag: BD, popularity: 0.65 },
  { code: 'ru', name: 'Russian', flag: RU, popularity: 0.6 },
  { code: 'pt', name: 'Portuguese', flag: PT, popularity: 0.55 },
  { code: 'ur', name: 'Urdu', flag: PK, popularity: 0.5 },
  { code: 'de', name: 'German', flag: DE, popularity: 0.45 },
  { code: 'ja', name: 'Japanese', flag: JP, popularity: 0.4 },
  { code: 'sw', name: 'Swahili', flag: TZ, popularity: 0.35 },
  { code: 'ko', name: 'Korean', flag: KR, popularity: 0.3 },
  { code: 'it', name: 'Italian', flag: IT, popularity: 0.25 },
];

const POPULAR_PRAYER_TOPICS = [
  { topic: 'Health', popularity: 1 },
  { topic: 'Family', popularity: 0.95 },
  { topic: 'Guidance', popularity: 0.9 },
  { topic: 'Peace', popularity: 0.85 },
  { topic: 'Strength', popularity: 0.8 },
  { topic: 'Forgiveness', popularity: 0.75 },
  { topic: 'Love', popularity: 0.7 },
  { topic: 'Faith', popularity: 0.65 },
  { topic: 'Hope', popularity: 0.6 },
  { topic: 'Wisdom', popularity: 0.55 },
  { topic: 'Protection', popularity: 0.5 },
  { topic: 'Healing', popularity: 0.45 },
  { topic: 'Gratitude', popularity: 0.4 },
  { topic: 'Patience', popularity: 0.35 },
  { topic: 'Courage', popularity: 0.3 },
  { topic: 'Prosperity', popularity: 0.25 },
  { topic: 'Comfort', popularity: 0.2 },
  { topic: 'Joy', popularity: 0.15 },
  { topic: 'Purpose', popularity: 0.1 },
  { topic: 'Relationships', popularity: 0.05 },
  { topic: 'Marriage', popularity: 0.9 },
  { topic: 'Success', popularity: 0.85 },
  { topic: 'Deliverance', popularity: 0.8 },
  { topic: 'Salvation', popularity: 0.75 },
  { topic: 'Anxiety', popularity: 0.7 },
  { topic: 'Depression', popularity: 0.65 },
  { topic: 'Clarity', popularity: 0.6 },
  { topic: 'Finances', popularity: 0.55 },
  { topic: 'Purity', popularity: 0.5 },
  { topic: 'Rest', popularity: 0.45 },
];

/** Feature cards shown in the "Everything you need" band. */
const FEATURES = [
  {
    icon: Sparkles,
    title: 'Personalized Prayers',
    body: 'Receive prayers crafted specifically for your unique situation and needs.',
  },
  {
    icon: Book,
    title: 'Biblical Wisdom',
    body: 'Each prayer is accompanied by a relevant Bible verse to inspire and guide you.',
  },
  {
    icon: Globe,
    title: 'Multi-Language Support',
    body: 'Access prayers in 15+ languages to serve a global community of believers.',
  },
];

/** The three-step explainer band. */
const STEPS_INFO = [
  {
    title: 'Share your heart',
    body: 'Tell us what you are going through — a worry, a prayer request, or simply a desire to grow.',
  },
  {
    title: 'Choose your style',
    body: 'Pick a focus topic, language and length that feels right for you.',
  },
  {
    title: 'Pray & reflect',
    body: 'Read your prayer and its verse. Save it to return to whenever you need.',
  },
];

/** Category grid — clicking one fills the topic input and scrolls to the form. */
const PRAYER_CATEGORIES = [
  {
    topic: 'Faith & Guidance',
    blurb: 'Seek a closer walk with God through prayer and divine guidance.',
    icon: Sunrise,
  },
  {
    topic: 'Healing & Health',
    blurb: 'Ask for strength through illness, recovery, and times of illness.',
    icon: HeartPulse,
  },
  {
    topic: 'Family & Relationships',
    blurb: 'Strengthen bonds with loved ones through unity, understanding, and love.',
    icon: Heart,
  },
  {
    topic: 'Gratitude',
    blurb: 'Give thanks for the blessings, big and small, that fill your days.',
    icon: Leaf,
  },
  {
    topic: 'Strength & Courage',
    blurb: 'Find courage to face hard seasons and the faith to keep going.',
    icon: Anchor,
  },
  {
    topic: 'Peace & Anxiety',
    blurb: 'Lay down worry and receive the peace that surpasses understanding.',
    icon: Star,
  },
  {
    topic: 'Work & Finances',
    blurb: 'Seek provision, integrity, and wisdom in your work and finances.',
    icon: Briefcase,
  },
  {
    topic: 'Studies & Exams',
    blurb: 'Calm nerves and ask for clarity, focus, and diligence in your studies.',
    icon: GraduationCap,
  },
  {
    topic: 'Forgiveness',
    blurb: 'Release hurt, make peace, and walk toward reconciliation.',
    icon: HandHeart,
  },
  {
    topic: 'Grief & Comfort',
    blurb: 'Find comfort and hope when your heart is heavy with loss.',
    icon: Sun,
  },
  {
    topic: 'Patience & Perseverance',
    blurb: 'Stay steady through long seasons of waiting and hard work.',
    icon: Clock,
  },
  {
    topic: 'Renewal & New Beginnings',
    blurb: 'Ask for a fresh start and the courage to step forward.',
    icon: Sparkle,
  },
];

interface FormData {
  name: string;
  city: string;
  prayerFor: string;
  prayerTopic: string;
  language: string;
}

interface PrayerResponse {
  prayer: string;
  bibleVerse: {
    text: string;
    reference: string;
  };
}

const NAV_LINKS = [
  { href: '#home', label: 'Home', icon: Home },
  { href: '#about', label: 'About', icon: Info },
  { href: '#features', label: 'Features', icon: Sparkles },
  { href: '#categories', label: 'Categories', icon: Book },
];

export default function PrayerPartnerWebsite() {
  const [step, setStep] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    city: '',
    prayerFor: '',
    prayerTopic: '',
    language: '',
  });
  const [prayerResponse, setPrayerResponse] = useState<PrayerResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isApiUnavailable, setIsApiUnavailable] = useState(false);
  const [verseOfTheDay, setVerseOfTheDay] = useState<{
    text: string;
    reference: string;
    version: string;
    notice?: string;
  }>({
    text: 'The Lord is near to all who call on Him, to all who call on Him in truth.',
    reference: 'Psalm 145:18',
    version: 'ESV',
  });
  const [isVerseLoading, setIsVerseLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchVerseOfTheDay();
  }, []);

  const fetchVerseOfTheDay = async () => {
    try {
      setIsVerseLoading(true);
      const res = await fetch('https://beta.ourmanna.com/api/v1/get?format=json&order=random', {
        headers: {
          accept: 'application/json',
        },
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      if (data?.verse?.details) {
        setVerseOfTheDay({
          text: data.verse.details.text,
          reference: data.verse.details.reference,
          version: data.verse.details.version || 'NIV',
          notice: data.verse.notice,
        });
      }
    } catch (err) {
      console.warn('Could not fetch daily verse from API, using fallback:', err);
    } finally {
      setIsVerseLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'radio' ? value : (name === 'prayerTopic' ? value : value.trim())
    }));
  };


  const nextStep = () => {
    if (step < STEPS.length - 1) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(prev => prev - 1);
    }
  };

  const extractJSONFromMarkdown = (text: string): string => {
    const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      try {
        return JSON.stringify(JSON.parse(jsonMatch[1]));
      } catch (e) {
        console.error('Failed to parse extracted JSON:', e);
        return '';
      }
    }
    return text;
  };

  /** Call secure Hostinger PHP proxy (all API keys strictly hidden on server). */
  const callGroqModel = async (model: string, prompt: string): Promise<string> => {
    const proxyRes = await fetch(API_PRAYER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, prompt }),
    });

    if (!proxyRes.ok) {
      let detail = '';
      try {
        const body = await proxyRes.json();
        detail = body?.error || body?.detail || '';
      } catch {
        detail = await proxyRes.text().catch(() => '');
      }
      const error = new Error(`Prayer service error (${proxyRes.status})${detail ? `: ${detail}` : ''}`);
      (error as Error & { status?: number }).status = proxyRes.status;
      throw error;
    }

    const proxyData = await proxyRes.json();
    if (!proxyData?.content || typeof proxyData.content !== 'string') {
      throw new Error('Prayer generation returned an empty response.');
    }
    return proxyData.content;
  };

  const generatePrayer = async () => {
    setIsLoading(true);
    setError('');

    try {
      const languageName =
        LANGUAGES.find(lang => lang.code === formData.language)?.name || 'English';

      // Explicit, unambiguous voice rules. "themselves" was too vague and made
      // the model narrate about the user in the third person.
      const isForSelf = formData.prayerFor === 'self';

      // The name and city must appear in EVERY prayer, self or others.
      const identityRules = `- You MUST include the name "${formData.name}" in the prayer, spelled exactly like this.
- You MUST include the city "${formData.city}" in the prayer, spelled exactly like this.
- Include both naturally — do not just list them as data.`;

      const perspectiveRules = isForSelf
        ? `CRITICAL VOICE RULES — the person praying IS ${formData.name}:
- Write entirely in the FIRST PERSON ("I", "me", "my"). ${formData.name} is praying to God about their own life.
- NEVER refer to the person praying in the third person. Do NOT write "for ${formData.name}", "guidance for ${formData.name}", or "Lord, grant ${formData.name.split(' ')[0]} wisdom".
- The prayer is spoken BY the person TO God. Use phrases like "I come before You", "grant me", "guide me".
- To include your own name, refer to yourself in a natural first-person way, for example: "I, ${formData.name} of ${formData.city}, come before You" or "${formData.name} of ${formData.city}, I humble myself before You". This must still read as first person.`
        : `CRITICAL VOICE RULES — the person praying is a third party, and ${formData.name} is the one being prayed for:
- Address ${formData.name} directly in the SECOND PERSON ("you", "your"). Begin with "Dear ${formData.name},".
- The one praying is a third party who is interceding. Use phrases like "we ask", "Lord, we pray for ${formData.name}", "may You bless them".
- NEVER write as if the person praying were ${formData.name}.
- Mention the city "${formData.city}" naturally in the prayer.`;

      const prompt = `Write a personalized prayer.

Details:
- Name: ${formData.name}
- City: ${formData.city}
- Topic: ${formData.prayerTopic}
- Written in: ${languageName}

${perspectiveRules}

CRITICAL — NAME AND CITY MUST APPEAR:
${identityRules}

Additional requirements:
- The prayer should be warm, personal and detailed, approximately 150-200 words.
- Also provide a relevant Bible verse with its reference.
- The prayer and the Bible verse must both be written in ${languageName}.
- Use natural paragraph breaks, not a list.

Format the response as a JSON object with the following structure:
{
  "prayer": "The generated prayer text in ${languageName}",
  "bibleVerse": {
    "text": "The Bible verse text in ${languageName}",
    "reference": "The Bible verse reference"
  }
}

Ensure that the response is a valid JSON object without any markdown formatting or special characters that could break JSON parsing.`;

      let lastError: Error | null = null;

      for (const model of GROQ_MODELS) {
        try {
          const raw = await callGroqModel(model, prompt);

          const jsonText = extractJSONFromMarkdown(raw);
          if (!jsonText) {
            throw new Error('Failed to extract valid JSON from the response');
          }
          const parsed: PrayerResponse = JSON.parse(jsonText);

          if (!parsed?.prayer || !parsed?.bibleVerse?.text || !parsed?.bibleVerse?.reference) {
            throw new Error('The prayer response was missing required fields');
          }

          setPrayerResponse(parsed);
          setIsApiUnavailable(false);
          nextStep();
          return;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error('Unknown error');

          const status = (lastError as Error & { status?: number }).status;

          // 401/403 = bad key: retrying another model will not help.
          if (status === 401 || status === 403) break;

          // Rate limits / server errors / network hiccups fall through to the next model.
          if (status === undefined || RETRYABLE_STATUSES.has(status)) {
            console.warn(`Model ${model} failed, trying next model...`, lastError);
            continue;
          }
          break;
        }
      }

      const message = lastError?.message || 'Unknown error';
      if (message.includes('API key') || message.includes('(401)') || message.includes('(403)')) {
        setError("We're experiencing technical difficulties with our prayer generation service. Please try again later or contact support if the issue persists.");
        setIsApiUnavailable(true);
      } else if (message.includes('(429)')) {
        setError('Our prayer service is busy right now (rate limited). Please wait a moment and try again.');
        setIsApiUnavailable(false);
      } else {
        setError(`Failed to generate prayer: ${message}. Please try again.`);
        setIsApiUnavailable(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const restartPrayer = () => {
    setStep(0);
    setFormData({
      name: '',
      city: '',
      prayerFor: '',
      prayerTopic: '',
      language: '',
    });
    setPrayerResponse(null);
    setError('');
  };

  /**
   * Choosing a category fills the topic and brings the user to the form.
   * We jump to the wizard and skip straight to the TOPIC step (skipping NAME)
   * so they land exactly where they need to act.
   */
  const chooseCategory = (topic: string) => {
    setFormData(prev => ({ ...prev, prayerTopic: topic }));
    setStep(1); // TOPIC
    document.getElementById('create')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isStepValid = () => {
    switch (STEPS[step]) {
      case 'NAME':
        return formData.name.trim() !== '' && formData.city.trim() !== '' && (formData.prayerFor === 'self' || formData.prayerFor === 'others');
      case 'TOPIC':
        return formData.prayerTopic.trim() !== '';
      case 'LANGUAGE':
        return formData.language !== '';
      default:
        return true;
    }
  };

  const sharePrayer = () => {
    if (prayerResponse) {
      const shareText = `🙏 My Personalized Prayer from Prayer Partner

Dear ${formData.name}'s Prayer:
${prayerResponse.prayer}

📖 Bible Verse:
${prayerResponse.bibleVerse.text}
- ${prayerResponse.bibleVerse.reference}

Generate your own prayer: https://prayerpartner.in`;

      if (navigator.share) {
        navigator.share({
          title: 'My Prayer from Prayer Partner',
          text: shareText,
          url: 'https://prayerpartner.in'
        }).catch(error => {
          console.error('Error sharing:', error);
          fallbackShare(shareText);
        });
      } else {
        fallbackShare(shareText);
      }
    }
  };

  const fallbackShare = (shareText: string) => {
    navigator.clipboard.writeText(shareText)
      .then(() => {
        alert('Prayer copied to clipboard! You can now share it with others.');
      })
      .catch(err => {
        console.error('Could not copy text:', err);
        alert('Could not copy the prayer. Please try again.');
      });
  };

  /** Copy just the prayer + verse, without the share preamble. */
  const copyPrayer = () => {
    if (prayerResponse) {
      const text = `${prayerResponse.prayer}\n\n${prayerResponse.bibleVerse.text}\n- ${prayerResponse.bibleVerse.reference}`;
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Prayer copied to clipboard!');
        })
        .catch(err => {
          console.error('Could not copy text:', err);
          alert('Could not copy the prayer. Please try again.');
        });
    }
  };

  // Read Aloud TTS feature has been removed as per user preference.

  const selectedLanguage = LANGUAGES.find(l => l.code === formData.language);

  const renderInput = () => {
    switch (STEPS[step]) {
      case 'NAME':
        return (
          <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="relative">
                <User className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" size={18} />
                <input
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                  required
                  aria-label="Your Name"
                />
              </div>
              <div className="relative">
                <Globe className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" size={18} />
                <input
                  name="city"
                  placeholder="Your City"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                  required
                  aria-label="Your City"
                />
              </div>
            </div>

            <fieldset>
              <legend className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                <Users size={15} className="text-ember-400" />
                Who is this prayer for?
              </legend>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'self', label: 'Myself', hint: 'My own journey' },
                  { value: 'others', label: 'Someone else', hint: 'On their behalf' },
                ].map(option => {
                  const active = formData.prayerFor === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`pp-focus cursor-pointer rounded-2xl border px-4 py-3.5 text-center transition-all duration-200 ${active
                        ? 'border-ember-400 bg-ember-50'
                        : 'border-ink-900/10 bg-cream-100 hover:border-ink-900/25 hover:bg-cream-200'
                        }`}
                    >
                      <input
                        type="radio"
                        name="prayerFor"
                        value={option.value}
                        checked={active}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <span className={`block font-semibold ${active ? 'text-ember-700' : 'text-ink-800'}`}>
                        {option.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-500">{option.hint}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </div>
        );
      case 'TOPIC':
        return (
          <div className="space-y-6">
            <div className="relative">
              <Book className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" size={18} />
              <input
                name="prayerTopic"
                placeholder="Enter your prayer topic"
                value={formData.prayerTopic}
                onChange={handleInputChange}
                className="input-field pl-12"
                required
                aria-label="Prayer Topic"
              />
            </div>
            <div>
              <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                Popular Prayer Topics
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {POPULAR_PRAYER_TOPICS.map((topic) => (
                  <motion.button
                    key={topic.topic}
                    type="button"
                    onClick={() => setFormData(prev => {
                      const currentTopics = prev.prayerTopic
                        .split(',')
                        .map(t => t.trim())
                        .filter(Boolean);
                      if (currentTopics.includes(topic.topic)) {
                        return {
                          ...prev,
                          prayerTopic: currentTopics.filter(t => t !== topic.topic).join(', ')
                        };
                      } else {
                        return {
                          ...prev,
                          prayerTopic: [...currentTopics, topic.topic].join(', ')
                        };
                      }
                    })}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.95 }}
                    className={`pp-focus rounded-full border px-3.5 py-1.5 font-semibold transition-colors duration-200 ${formData.prayerTopic.split(',').map(t => t.trim()).includes(topic.topic)
                      ? 'border-transparent bg-ember-sheen text-white shadow-glow'
                      : 'border-ink-900/10 bg-cream-100 text-ink-800 hover:border-ember-300 hover:bg-ember-50 hover:text-ember-800'
                      }`}
                    style={{
                      fontSize: `${Math.max(0.8, Math.min(1.2, 1 + topic.popularity * 0.4))}rem`,
                    }}
                  >
                    {topic.topic}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        );
      case 'LANGUAGE':
        return (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {LANGUAGES.map((lang) => {
              const FlagComponent = lang.flag;
              const active = formData.language === lang.code;
              return (
                <motion.label
                  key={lang.code}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className={`pp-focus relative flex cursor-pointer flex-col items-center gap-2 rounded-2xl border p-3.5 transition-all duration-200 ${active
                    ? 'border-ember-400 bg-ember-50'
                    : 'border-ink-900/10 bg-cream-100 hover:border-ink-900/25 hover:bg-cream-200'
                    }`}
                >
                  <input
                    type="radio"
                    name="language"
                    value={lang.code}
                    checked={active}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  {active && (
                    <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-ember-500 text-white">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  )}
                  <FlagComponent className="h-8 w-12 rounded-md shadow-md ring-1 ring-ink-900/10" />
                  <span className={`text-center text-xs font-semibold leading-tight ${active ? 'text-ember-700' : 'text-ink-600'}`}>
                    {lang.name}
                  </span>
                </motion.label>
              );
            })}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-clip">
      {/* Ambient canvas: sunrise sky + angelic rays (single source of truth) */}
      <div className="pp-canvas" aria-hidden="true" />
      <div className="pp-rays" aria-hidden="true" />

      {/* Ambient canvas: sunrise sky + angelic rays (single source of truth) */}
      <div className="pp-canvas" aria-hidden="true" />
      <div className="pp-rays" aria-hidden="true" />

      {/* Dynamic Aurora Layers & Ethereal Glow Streams */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
        {/* Primary flowing emerald/mint aurora ribbon */}
        <div className="animate-aurora-wave-1 absolute -left-[20%] -top-[15%] h-[80vh] w-[85vw] rounded-full bg-gradient-to-tr from-aurora-300/35 via-aurora-400/25 to-transparent blur-[110px]" />

        {/* Secondary lilac & lavender celestial ribbon */}
        <div
          className="animate-aurora-wave-2 absolute -right-[15%] top-[10%] h-[75vh] w-[75vw] rounded-full bg-gradient-to-bl from-aurora-lilac/45 via-indigo-200/30 to-transparent blur-[120px]"
        />

        {/* Warm sunrise amber/peach glow orb */}
        <div
          className="animate-aurora-pulse absolute left-[20%] top-[25%] h-[50vh] w-[50vw] rounded-full bg-gradient-to-r from-aurora-peach/40 via-gold-200/35 to-transparent blur-[90px]"
        />

        {/* Lower blush accent orb */}
        <div
          className="animate-aurora-wave-1 absolute -left-[10%] bottom-[-10%] h-[60vh] w-[60vw] rounded-full bg-gradient-to-tr from-aurora-blush/40 via-ember-200/25 to-transparent blur-[100px]"
          style={{ animationDelay: '-12s' }}
        />
      </div>

      <a
        href="#home"
        className="pp-focus sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-ember-500 focus:px-4 focus:py-2 focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>

      {/* ---------------- Header ---------------- */}
      <header className="pp-glass z-50 relative md:sticky md:top-0 border-b border-white/60">
        <div className="container flex items-center justify-between gap-4 py-3.5">
          <a href="#home" className="pp-focus group flex items-center gap-2.5 rounded-xl pr-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-indigo-900 shadow-sm transition-transform duration-300 group-hover:scale-105">
              <Feather className="text-gold-200" size={17} strokeWidth={2.2} />
            </span>
            <span className="font-display text-base font-bold tracking-tight text-ink-900">
              Prayer Partner
            </span>
          </a>

          <nav className="hidden items-center gap-6 md:flex" aria-label="Primary">
            {NAV_LINKS.map(link => (
              <a
                key={link.href}
                href={link.href}
                className="pp-focus relative rounded-lg px-2 py-1.5 text-[0.82rem] font-semibold text-ink-600 transition-colors duration-200 hover:text-ink-950 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:bg-ember-500 after:transition-transform after:duration-300 hover:after:scale-x-100"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block [&_iframe]:h-8 [&_iframe]:opacity-80 [&_iframe]:transition-opacity hover:[&_iframe]:opacity-100">
              <KofiButton username="R5R815RCDR" label="Donate" preset="thin" backgroundColor="#e04a11" />
            </span>
            <a
              href="#create"
              className="pp-focus rounded-full bg-indigo-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-indigo-800 hover:shadow-lift"
            >
              Start praying
            </a>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {/* ---------------- Hero ---------------- */}
        {/* ---------------- Hero Variant Switcher Toolbar ---------------- */}
        {/* ---------------- Hero Section ---------------- */}
        <section id="home" className="relative overflow-hidden pb-20 pt-10 md:pb-28 md:pt-16">
          {/* Enhanced Multi-layer Aurora Backlight */}
          <div
            className="pointer-events-none absolute left-1/2 top-[-10rem] -z-10 h-[40rem] w-[70rem] -translate-x-1/2 rounded-full bg-gradient-to-b from-gold-300/35 via-aurora-peach/30 to-transparent blur-[120px]"
            aria-hidden="true"
          />
          <div
            className="animate-aurora-pulse pointer-events-none absolute left-1/2 top-[4rem] -z-10 h-[26rem] w-[38rem] -translate-x-1/2 rounded-full bg-gradient-to-r from-aurora-300/35 via-ember-300/25 to-aurora-lilac/35 blur-[95px]"
            aria-hidden="true"
          />

          <div className="container mx-auto px-4">
            <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
              {/* Left Column: Bold Copy & Actions */}
              <div className="text-center lg:text-left lg:col-span-7">
                <div className="inline-flex items-center gap-2.5 rounded-full pp-aurora-badge px-4 py-1.5 text-xs font-bold text-ember-800 shadow-sm">
                  <span className="flex h-2 w-2 rounded-full bg-ember-500 animate-ping" />
                  <span>AI Spiritual Companion</span>
                  <Sparkles size={13} className="text-ember-600" />
                </div>

                <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-ink-950 sm:text-5xl lg:text-6xl leading-[1.12]">
                  When you can’t find the words,{' '}
                  <span className="pp-gradient-text block">we’ll help you pray.</span>
                </h1>

                <p className="mt-5 max-w-xl text-lg text-ink-600 leading-relaxed mx-auto lg:mx-0">
                  Personalized prayers and scripture generated with tenderness for your exact moment. Completely private, compassionate, and always free.
                </p>

                <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-4">
                  <a
                    href="#create"
                    className="pp-focus group relative inline-flex items-center gap-2.5 rounded-full bg-ember-sheen px-8 py-4 font-display text-base font-bold text-white shadow-glow transition-all duration-300 hover:brightness-110 hover:scale-[1.02]"
                  >
                    <Sparkles size={18} />
                    <span>Start Your Prayer</span>
                    <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                  </a>
                  <a
                    href="#how"
                    className="pp-focus inline-flex items-center gap-2 rounded-full pp-glass-pill px-7 py-4 font-display text-base font-bold text-indigo-900 shadow-sm transition-all duration-300 hover:bg-white"
                  >
                    <Book size={17} className="text-indigo-600" />
                    <span>See How It Works</span>
                  </a>
                </div>

                {/* Feature tags */}
                <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
                  {[
                    { icon: ShieldCheck, label: 'No Signup Required' },
                    { icon: Globe, label: '15+ Languages' },
                    { icon: HandHeart, label: 'Made for Shy Hearts' },
                  ].map(item => (
                    <span
                      key={item.label}
                      className="pp-glass-pill inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold text-ink-700 shadow-sm"
                    >
                      <item.icon size={14} className="text-ember-600" />
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right Column: Verse of the Day Glass Card */}
              <div className="lg:col-span-5 relative">
                {/* Glowing aurora backdrop behind the card */}
                <div className="absolute -inset-2 rounded-[2.5rem] bg-gradient-to-r from-ember-300/30 via-gold-300/30 to-aurora-300/30 blur-xl opacity-75" />

                <div className="relative rounded-[2rem] pp-glass-card p-6 sm:p-7 shadow-lift border border-white/90">
                  {/* Card Header: Verse of the Day */}
                  <div className="flex items-center justify-between pb-4 border-b border-ink-900/10">
                    <div className="flex items-center gap-3">
                      <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold-100 text-gold-700 ring-1 ring-gold-200">
                        <Book size={20} />
                      </span>
                      <div>
                        <h4 className="font-display font-bold text-ink-900 text-sm">Verse of the Day</h4>
                        <p className="text-[0.72rem] text-gold-700 font-semibold">Daily Scripture & Reflection</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={fetchVerseOfTheDay}
                        title="Refresh daily verse"
                        disabled={isVerseLoading}
                        className="pp-focus p-1.5 rounded-full text-ink-500 hover:text-ink-800 hover:bg-white/60 transition-colors"
                      >
                        <RefreshCw size={12} className={isVerseLoading ? 'animate-spin text-ember-500' : ''} />
                      </button>
                      <span className="pp-glass-pill px-2.5 py-1 rounded-full text-[0.68rem] font-bold text-amber-800 flex items-center gap-1.5 border border-gold-300/40">
                        <Sun size={12} className="text-amber-500" />
                        Today
                      </span>
                    </div>
                  </div>

                  {/* Main Verse Box */}
                  <div className="mt-4 space-y-3">
                    <div className="rounded-2xl border border-gold-300/50 bg-gradient-to-br from-gold-50/70 via-cream-100/50 to-white/60 p-5 shadow-sm min-h-[110px] flex flex-col justify-between">
                      {isVerseLoading ? (
                        <div className="flex items-center justify-center py-6 text-sm text-ink-500 gap-2">
                          <Loader className="animate-spin text-gold-600" size={18} />
                          <span>Loading daily verse…</span>
                        </div>
                      ) : (
                        <>
                          <p className="text-base sm:text-lg font-display italic text-ink-900 leading-relaxed">
                            “{verseOfTheDay.text}”
                          </p>
                          <div className="mt-3 flex items-center justify-between">
                            <p className="font-bold text-gold-800 text-xs uppercase tracking-wider">
                              — {verseOfTheDay.reference}
                            </p>
                            <span className="text-[0.68rem] font-semibold text-ink-500 uppercase tracking-widest bg-white/70 px-2 py-0.5 rounded-md border border-ink-900/5">
                              {verseOfTheDay.version}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Reflection / Prayer Prompt */}
                    <div className="rounded-xl border border-white/70 bg-white/55 p-3.5 text-xs text-ink-600 leading-relaxed flex items-start gap-2.5">
                      <Sparkles size={16} className="text-ember-500 shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink-900">Today's Reflection:</strong> Let this scripture settle in your heart. When you are ready, bring these words into your personal conversation with God today.
                      </span>
                    </div>
                  </div>

                  {/* Footer link to create prayer */}
                  <div className="mt-5 pt-3.5 border-t border-ink-900/10 flex items-center justify-between">
                    <span className="text-xs text-ink-500">Need comfort today?</span>
                    <a
                      href="#create"
                      className="pp-focus text-xs font-bold text-ember-600 hover:text-ember-700 inline-flex items-center gap-1 transition-transform hover:translate-x-0.5"
                    >
                      Turn this into a personal prayer <ChevronRight size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Create / Wizard ---------------- */}
        <section id="create" className="relative scroll-mt-24 py-20 md:py-28">
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-16 items-start">
              {/* Sticky Left Sidebar */}
              <div className="lg:sticky lg:top-32">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.35em] text-gold-600">
                  Pray with us
                </p>
                <h2 className="text-ink-900 mt-4 font-display text-4xl font-bold sm:text-5xl leading-tight">
                  Create your personal prayer
                </h2>
                <p className="mt-6 text-base leading-[1.8] text-ink-600">
                  Share what's on your heart. We'll write a prayer just for you, with a Bible verse —
                  and read it aloud if you like.
                </p>

                {/* Visual anchor / graphic with glassmorphism */}
                <div className="mt-10 hidden lg:block h-48 w-full rounded-3xl pp-glass-card p-6 relative overflow-hidden shadow-card">
                  <div className="absolute -right-8 -bottom-8 opacity-25">
                    <Feather size={160} className="text-gold-500" strokeWidth={1} />
                  </div>
                  <h4 className="font-display text-lg font-bold text-ink-800">Your quiet sanctuary</h4>
                  <p className="mt-2 text-sm text-ink-600 relative z-10 leading-relaxed">
                    Take a deep breath. Focus on your intentions. Let the words flow naturally in complete peace and privacy.
                  </p>
                </div>
              </div>

              {/* Form Area */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="pp-glass-card overflow-hidden rounded-[2.5rem] shadow-lift ring-1 ring-white/60"
              >
                {/* Stepper rail */}
                <div className="border-b border-ink-900/10 bg-cream-50 px-5 py-6 md:px-8">
                  <div className="relative">
                    <div className="absolute left-0 right-0 top-5 hidden h-px bg-ink-900/[0.06] md:block" />
                    <motion.div
                      className="absolute left-0 top-5 hidden h-px bg-ember-sheen md:block"
                      initial={{ width: 0 }}
                      animate={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />

                    <ol className="relative grid grid-cols-4 gap-2">
                      {STEPS.map((s, index) => {
                        const state = index < step ? 'done' : index === step ? 'current' : 'todo';
                        return (
                          <li key={s} className="flex flex-col items-center gap-2 text-center">
                            <motion.span
                              whileHover={{ scale: 1.08 }}
                              whileTap={{ scale: 0.95 }}
                              className={`pp-focus grid h-10 w-10 place-items-center rounded-full border text-sm font-bold transition-colors duration-300 ${state === 'done'
                                ? 'border-transparent bg-ember-500 text-white'
                                : state === 'current'
                                  ? 'border-ember-400 bg-ember-500/20 text-ember-800 shadow-[0_0_0_4px_rgba(242,96,31,0.15)]'
                                  : 'border-ink-900/10 bg-white text-ink-400'
                                }`}
                            >
                              {state === 'done' ? <Check size={16} strokeWidth={3} /> : index + 1}
                            </motion.span>
                            <span
                              className={`text-[0.7rem] font-bold uppercase tracking-wider transition-colors duration-300 sm:text-xs ${state === 'todo' ? 'text-ink-400' : 'text-ink-900'
                                }`}
                            >
                              {STEP_META[s].label}
                            </span>
                            <span className="hidden text-[0.7rem] text-ink-400 sm:block">
                              {STEP_META[s].hint}
                            </span>
                          </li>
                        );
                      })}
                    </ol>

                    {/* Progress bar (mobile) */}
                    <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-ink-900/[0.06] md:hidden">
                      <motion.div
                        className="h-full rounded-full bg-ember-sheen"
                        initial={{ width: 0 }}
                        animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Step body */}
                <div className="px-5 py-8 md:px-8 md:py-10">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.28 }}
                      className="space-y-8"
                    >
                      <div className="text-center">
                        <h2 className="font-display text-2xl font-bold text-ink-900 sm:text-3xl">
                          {PROMPTS[STEPS[step]]}
                        </h2>
                        <p className="mt-2 text-sm text-ink-500">
                          Step {step + 1} of {STEPS.length}
                        </p>
                      </div>

                      {STEPS[step] !== 'PRAYER' ? (
                        renderInput()
                      ) : (
                        <div className="space-y-6">
                          {prayerResponse && (
                            <>
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 }}
                                className="relative overflow-hidden rounded-3xl border border-ember-200 bg-gradient-to-br from-ember-50 to-cream-100 p-6 sm:p-8"
                              >
                                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-ember-50 blur-3xl" aria-hidden="true" />
                                <div className="relative flex flex-wrap items-center justify-between gap-3">
                                  <h3 className="flex items-center gap-2.5 font-display text-xl font-bold text-ember-800 sm:text-2xl">
                                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-ember-500/20">
                                      <Sparkles size={18} className="text-ember-600" />
                                    </span>
                                    Your Personalized Prayer
                                  </h3>
                                  {selectedLanguage && (
                                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-1 text-xs font-semibold text-ink-600">
                                      <selectedLanguage.flag className="h-4 w-6 rounded-sm" />
                                      {selectedLanguage.name}
                                    </span>
                                  )}
                                </div>
                                <p className="relative mt-5 whitespace-pre-line text-base leading-[1.9] text-ink-800 sm:text-lg">
                                  {prayerResponse.prayer}
                                </p>
                              </motion.div>

                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="rounded-3xl border border-gold-300 bg-gradient-to-br from-gold-50 to-cream-100 p-6 sm:p-8"
                              >
                                <h3 className="flex items-center gap-2.5 font-display text-xl font-bold text-gold-700 sm:text-2xl">
                                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-gold-100">
                                    <Book size={18} className="text-gold-700" />
                                  </span>
                                  Relevant Bible Verse
                                </h3>
                                <blockquote className="mt-5 border-l-2 border-gold-300 pl-5">
                                  <p className="text-base italic leading-[1.9] text-ink-800 sm:text-lg">
                                    {prayerResponse.bibleVerse.text}
                                  </p>
                                  <footer className="mt-3 text-sm font-bold uppercase tracking-[0.15em] text-gold-700">
                                    — {prayerResponse.bibleVerse.reference}
                                  </footer>
                                </blockquote>
                              </motion.div>

                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.45 }}
                                className="flex flex-wrap items-center justify-center gap-2.5"
                              >
                                <button
                                  type="button"
                                  onClick={copyPrayer}
                                  className="pp-focus inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-cream-100 px-5 py-2.5 text-sm font-semibold text-ink-800 transition-colors hover:border-ink-900/30 hover:bg-ink-900/[0.05]"
                                >
                                  <Copy size={15} />
                                  Copy text
                                </button>
                              </motion.div>
                            </>
                          )}
                        </div>
                      )}

                      {error && (
                        <div
                          role="alert"
                          className="rounded-2xl border border-red-300 bg-red-50 p-5 text-center"
                        >
                          <p className="font-semibold text-red-700">{error}</p>
                          <button
                            type="button"
                            onClick={generatePrayer}
                            className="pp-focus mt-4 inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-red-600"
                          >
                            <RefreshCw size={15} />
                            Retry
                          </button>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* ---------- Wizard actions ---------- */}
                  <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-ink-900/10 pt-7">
                    {step > 0 && step < STEPS.length - 1 ? (
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={prevStep}
                        className="pp-focus inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-cream-100 px-5 py-3 text-sm font-semibold text-ink-600 transition-colors hover:border-ink-900/30 hover:bg-ink-900/[0.05]"
                      >
                        <ArrowLeft size={16} />
                        Back
                      </motion.button>
                    ) : (
                      <span />
                    )}

                    {step < STEPS.length - 2 && (
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={nextStep}
                        disabled={!isStepValid()}
                        className={`pp-focus ml-auto inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-display text-base font-bold text-white transition-all duration-300 ${isStepValid()
                          ? 'bg-ember-sheen shadow-glow hover:brightness-110'
                          : 'cursor-not-allowed bg-ink-900/[0.06] text-ink-400 shadow-none'
                          }`}
                      >
                        Next
                        <ChevronRight size={18} />
                      </motion.button>
                    )}

                    {step === STEPS.length - 2 && (
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={generatePrayer}
                        disabled={isLoading || !isStepValid() || isApiUnavailable}
                        className={`pp-focus ml-auto inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-display text-base font-bold text-white transition-all duration-300 ${isLoading || !isStepValid() || isApiUnavailable
                          ? 'cursor-not-allowed bg-ink-900/[0.06] text-ink-400 shadow-none'
                          : 'bg-ember-sheen shadow-glow hover:brightness-110'
                          }`}
                      >
                        {isLoading ? (
                          <>
                            <Loader className="animate-spin" size={18} />
                            Generating…
                          </>
                        ) : isApiUnavailable ? (
                          'Service Unavailable'
                        ) : (
                          <>
                            <Sparkles size={18} />
                            Generate Prayer
                          </>
                        )}
                      </motion.button>
                    )}

                    {step === STEPS.length - 1 && (
                      <div className="ml-auto flex flex-wrap gap-3">
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          onClick={restartPrayer}
                          className="pp-focus inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-cream-100 px-6 py-3.5 font-display text-base font-bold text-ink-800 transition-colors hover:border-ink-900/30 hover:bg-ink-900/[0.05]"
                        >
                          <RefreshCw size={17} />
                          New Prayer
                        </motion.button>
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          onClick={sharePrayer}
                          className="pp-focus inline-flex items-center gap-2 rounded-full bg-ember-sheen px-7 py-3.5 font-display text-base font-bold text-white shadow-glow transition-all hover:brightness-110"
                        >
                          <Share2 size={17} />
                          Share Your Prayer
                        </motion.button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ---------------- Our Story ---------------- */}
        <section id="about" className="relative scroll-mt-24 bg-white/40 py-20 backdrop-blur-[2px] md:py-28">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.35em] text-ember-700">
                Our story
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
                About Prayer Partner
              </h2>
              <p className="mx-auto mt-6 text-[0.95rem] leading-[1.9] text-ink-600">
                The main reason behind starting Prayer Partner is to help those people who are shy,
                and they hesitate and those who really want to pray but they don't know how to
                connect with God. This is where Prayer Partner took inception. Prayer Partner is your
                personal companion for spiritual growth and reflection. Our AI-powered platform
                generates personalized prayers tailored to your specific needs and circumstances,
                helping you deepen your connection with your faith.
              </p>

              <p className="mx-auto mt-8 max-w-xl font-display text-lg italic leading-relaxed text-indigo-700">
                “You don't need the right words. You only need the willingness to begin.”
              </p>
            </div>

            {/* Trust stats */}
            <div className="mx-auto mt-14 grid max-w-3xl grid-cols-2 gap-6 sm:grid-cols-4">
              {[
                { value: '15+', label: 'Languages' },
                { value: '20', label: 'Prayer topics' },
                { value: '100%', label: 'Free to use' },
                { value: '0', label: 'Account needed' },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="rounded-2xl pp-glass-pill px-4 py-5 text-center shadow-card transition-all duration-300 hover:scale-105"
                >
                  <p className="pp-gradient-text font-display text-2xl font-bold">{stat.value}</p>
                  <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-wider text-ink-500">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- Features ---------------- */}
        <section id="features" className="scroll-mt-24 py-20 md:py-28">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-ink-900 font-display text-3xl font-bold sm:text-4xl">
                Everything you need to pray with confidence
              </h2>
            </div>

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map(item => (
                <div
                  key={item.title}
                  className="group rounded-3xl pp-glass-card p-7 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:border-ember-300/50 hover:shadow-lift"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-aurora-200/70 text-indigo-700 transition-colors duration-300 group-hover:bg-ember-100 group-hover:text-ember-700">
                    <item.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-ink-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-500">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- How it works ---------------- */}
        <section id="how" className="relative scroll-mt-24 overflow-hidden py-20 text-ink-900 md:py-28 pp-glass border-y border-white/60">
          {/* subtle gold glow */}
          <div
            className="pointer-events-none absolute right-0 top-0 h-[30rem] w-[30rem] -translate-y-1/2 translate-x-1/3 rounded-full bg-gold-300/15 blur-[100px]"
            aria-hidden="true"
          />
          <div className="container relative">
            <div className="mx-auto max-w-xl text-center">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.35em] text-gold-600">
                Simple by design
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl text-ink-900">
                How it works
              </h2>
            </div>

            <ol className="mt-14 grid gap-10 sm:grid-cols-3">
              {STEPS_INFO.map((item, i) => (
                <li key={item.title} className="text-center relative">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl pp-glass-pill shadow-card font-display text-lg font-bold text-gold-600 ring-1 ring-gold-200/50">
                    {i + 1}
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold text-ink-900">{item.title}</h3>
                  <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-ink-600">
                    {item.body}
                  </p>
                  {i < STEPS_INFO.length - 1 && (
                    <span
                      className="absolute top-7 left-[60%] hidden w-[80%] border-t border-dashed border-ink-900/15 sm:block"
                      aria-hidden="true"
                    />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------------- Categories ---------------- */}
        <section id="categories" className="scroll-mt-24 py-20 md:py-28">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.35em] text-ember-700">
                Find your focus
              </p>
              <h2 className="text-ink-900 mt-3 font-display text-3xl font-bold sm:text-4xl">
                Prayers for every season of life
              </h2>
            </div>

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {PRAYER_CATEGORIES.map(cat => (
                <button
                  key={cat.topic}
                  type="button"
                  onClick={() => chooseCategory(cat.topic)}
                  className="pp-focus group flex flex-col items-start rounded-3xl pp-glass-card p-6 text-left shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:border-ember-300/50 hover:shadow-lift"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-aurora-100 text-indigo-700 ring-1 ring-aurora-200 transition-colors duration-300 group-hover:bg-ember-100 group-hover:text-ember-700 group-hover:ring-ember-200">
                    <cat.icon size={20} />
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold text-ink-900">{cat.topic}</h3>
                  <p className="mt-1.5 flex-1 text-sm leading-relaxed text-ink-500">{cat.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-ember-700 transition-transform duration-300 group-hover:translate-x-1">
                    Pray about this
                    <ChevronRight size={13} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

      </main>
      {/* ---------------- Footer ---------------- */}
      <footer className="border-t border-ink-900/10 bg-indigo-950 text-indigo-100">
        <div className="container py-14 pb-24 md:pb-14">
          <div className="flex flex-col items-center gap-8 text-center">
            {/* Brand */}
            <div>
              <a href="#home" className="pp-focus inline-flex items-center gap-2.5 rounded-xl">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-ember-sheen">
                  <Feather className="text-white" size={18} strokeWidth={2.2} />
                </span>
                <span className="font-display text-xl font-bold text-white">Prayer Partner</span>
              </a>
              <p className="mt-3 text-sm text-indigo-300">
                Your companion for spiritual growth and reflection.
              </p>

              {/* Contact Details Below Logo Text */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-sm text-indigo-200">
                <a
                  href="mailto:anuj@ssinternet.in"
                  className="pp-focus inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 ring-1 ring-white/10 transition-all hover:bg-white/10 hover:text-gold-200"
                >
                  <Mail size={16} className="text-ember-400" />
                  <span>anuj@ssinternet.in</span>
                </a>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 ring-1 ring-white/10">
                  <MapPin size={16} className="text-gold-400" />
                  <span>Kota, Rajasthan, India</span>
                </div>
              </div>
            </div>

            {/* Links */}
            <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3" aria-label="Footer">
              {NAV_LINKS.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  className="pp-focus rounded text-sm font-semibold text-indigo-200 transition-colors hover:text-gold-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Donate */}
            <div className="[&_iframe]:h-9">
              <KofiButton
                username="R5R815RCDR"
                label="Support us"
                preset="thin"
                backgroundColor="#e04a11"
              />
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-7 text-xs text-indigo-400 sm:flex-row">
            <p>
              &copy;2024-2026 Prayer Partner by{' '}
              <a
                href="https://ssinternet.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline transition-colors hover:text-gold-200"
              >
                SS Internet Services
              </a>
              . All rights reserved.
            </p>
            <p className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 animate-breathe rounded-full bg-gold-300" />
              Made with faith, for everyone
            </p>
          </div>
        </div>
      </footer>

      {/* ---------------- Mobile bottom nav ---------------- */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-ink-900/10 bg-cream-50/95 backdrop-blur-md shadow-card md:hidden"
        aria-label="Mobile"
      >
        <div className="grid h-16 grid-cols-5 items-center">
          {/* Left links: Home & About */}
          {NAV_LINKS.slice(0, 2).map(link => (
            <a
              key={link.href}
              href={link.href}
              className="pp-focus flex flex-col items-center justify-center gap-1 text-ink-500 transition-colors active:text-ember-600"
            >
              <link.icon size={19} />
              <span className="text-[0.62rem] font-semibold">{link.label}</span>
            </a>
          ))}

          {/* Center Pray CTA button */}
          <div className="flex items-center justify-center">
            <a
              href="#create"
              className="pp-focus group flex h-12 w-12 flex-col items-center justify-center rounded-full bg-gradient-to-tr from-ember-600 via-ember-500 to-gold-400 text-white shadow-glow transition-transform duration-200 active:scale-95"
              aria-label="Generate Prayer"
            >
              <Sparkles size={18} className="transition-transform group-hover:rotate-12" />
              <span className="text-[0.55rem] font-extrabold uppercase tracking-wider leading-none mt-0.5">
                Pray
              </span>
            </a>
          </div>

          {/* Right links: Features & Categories */}
          {NAV_LINKS.slice(2, 4).map(link => (
            <a
              key={link.href}
              href={link.href}
              className="pp-focus flex flex-col items-center justify-center gap-1 text-ink-500 transition-colors active:text-ember-600"
            >
              <link.icon size={19} />
              <span className="text-[0.62rem] font-semibold">{link.label}</span>
            </a>
          ))}
        </div>
      </nav>
    </div>
  );
}

